import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { cloneDeep } from 'lodash-es';

import { API_URL } from '@constants';
import {
  ApprovalUserLog,
  File as FileModel,
  FileProcessType,
  FileSubType,
  FileVersionSendReceipt,
  Version,
  VisibleSignature,
} from '@models';
import { ModalService } from './modal.service';

interface OngoingApproval {
  rid: number;
  displayName: string;
  format?: string;
  version: number;
  status: number;
  approvalSignature?: number;
  signature: boolean;
  updateDate: number;
}

interface FileExistsResponse {
  alreadyExist?: boolean;
  fileName?: string;
  rid?: number;
}

export interface PrivasphereAuthority {
  displayName: string;
  email: string;
  mobilephone?: string;
}

interface PrivasphereAuthorityFilterItem {
  name: string;
  code: string;
}

interface PrivasphereAuthorityCategory {
  name: string;
  filters: PrivasphereAuthorityFilterItem[];
}

@Service()
export class FileService {
  modalService = inject(ModalService);
  http = inject(HttpClient);

  getFile = (rid: number) =>
    this.http.get<FileModel>(`${API_URL}/files/${rid}`);

  signFile = (
    rid: number,
    signatureLevel: number,
    params: Nullable<VisibleSignature>
  ) =>
    this.http.post<FileModel>(
      `${API_URL}/files/${rid}/sign?signature=${signatureLevel}`,
      params
    );

  getUserSignatureImage = (userId: number, params: string) =>
    this.http.get(
      `${API_URL}/users/${userId}/signatureimage?base64=true${params}`,
      { responseType: 'text' }
    );

  checkFileExists = (pathName: string, body: Record<string, unknown>) =>
    this.http.post<FileExistsResponse>(
      `${API_URL}/${pathName}/files/exists`,
      body
    );

  uploadFile = (pathName: string, formData: FormData) =>
    this.http.post<FileModel>(`${API_URL}/${pathName}`, formData, {
      reportUploadProgress: true,
      observe: 'events',
    });

  createVersion = (rid: number, body: { versionDescription: string }) =>
    this.http.post<FileModel>(`${API_URL}/files/${rid}/versions`, body);

  getPlaceholderImageData = (approverRid: number, isActiveSigner: boolean) =>
    this.http.get(
      `${API_URL}/users/${approverRid}/placeholder?base64=true&active=${isActiveSigner}`,
      {
        responseType: 'text',
      }
    );

  getVersionSendReceipts = (
    rid: number,
    currentVersion: number,
    refresh: boolean = false
  ) =>
    this.http.get<FileVersionSendReceipt[]>(
      `${API_URL}/files/${rid}/versions/${currentVersion}/sendreceipts?refresh=${refresh}`
    );

  postFileVote = (url: string, params: Record<string, unknown>) =>
    this.http.post<FileModel>(url, params);

  getVersions = (rid: number) =>
    this.http.get<Version[]>(`${API_URL}/files/${rid}/versions`);

  getVersion = (rid: number, version: number) =>
    this.http.get<Version>(`${API_URL}/files/${rid}/versions/${version}`);

  downloadProtectedFile = (rid: number, password: string) =>
    this.http.get(`${API_URL}/files/${rid}/download?password=${password}`, {
      responseType: 'blob',
      observe: 'response',
    });

  restoreFileVersion = (
    rid: number,
    version: number,
    versionDescription?: string
  ) => {
    const body = versionDescription ? { versionDescription } : {};

    return this.http.post<FileModel>(
      `${API_URL}/files/${rid}/versions/${version}/restore`,
      body
    );
  };

  getProtectedFileVersion = (rid: number, version: number, password: string) =>
    this.http.get(
      `${API_URL}/files/${rid}/versions/${version}/xod.xod?password=${password}`,
      { responseType: 'blob', observe: 'response' }
    );

  updateFile = (
    type: string,
    body: {
      rid: number;
      name: string;
      description: string;
      visibleFrom: number;
      maxVersions: number;
      expireDate: number;
      updateDate: number;
    }
  ) => this.http.put<FileModel>(`${API_URL}/${type}/${body.rid}`, body);

  uploadFileVersion = (rid: number, formData: FormData) =>
    this.http.post<FileModel>(`${API_URL}/files/${rid}/versions`, formData, {
      reportUploadProgress: true,
      observe: 'events',
    });

  updateFileVersionDescription = (
    rid: number,
    version: number,
    versionDescription: string
  ) =>
    this.http.put<Version>(`${API_URL}/files/${rid}/versions/${version}`, {
      versionDescription,
    });

  sendFileViaPrivasphere = (rid: number, data: Record<string, unknown>) =>
    this.http.post<{ success: boolean }>(`${API_URL}/files/${rid}/send`, data);

  getPrivasphereAuthorities = (params?: Record<string, string | number>) =>
    this.http.get<PrivasphereAuthority[]>(
      `${API_URL}/system/privasphereauthorities/`,
      params ? { params } : {}
    );

  getPrivasphereAuthorityFilters = () =>
    this.http.get<PrivasphereAuthorityCategory[]>(
      `${API_URL}/system/privasphereauthoritiesfilters`
    );

  createShortcut = (
    path: string,
    parentRid: number,
    params: Record<string, unknown>
  ) =>
    this.http.post<FileModel>(`${API_URL}/${path}/${parentRid}/files`, params);

  signSeal = (formData: FormData) =>
    this.http.post<FileModel>(`${API_URL}/files/signseal`, formData);

  openFile = async (
    rid: number,
    processType?: FileProcessType,
    isSigning?: boolean
  ) => {
    const { FileComponent } =
      await import('../../modules/file/components/file');

    return this.modalService.open(
      FileComponent,
      {
        resourceRid: rid,
        processType,
        isSigning,
      },
      {
        modalDialogClass: 'file-modal',
      }
    );
  };

  openDownloadLockedFileModal = async (resource: FileModel) => {
    const { DownloadLockedFileModalComponent } =
      await import('../../shared/components/file/download-locked-file-modal.component');

    return this.modalService.open(DownloadLockedFileModalComponent, {
      resource: cloneDeep(resource),
    });
  };

  openDownloadAntiVirusLimitExceededFileModal = async (resource: FileModel) => {
    const { DownloadAntivirusLimitExceededFileModalComponent } =
      await import('../../shared/components/file/download-antivirus-limit-exceeded-file-modal.component');

    return this.modalService.open(
      DownloadAntivirusLimitExceededFileModalComponent,
      {
        resource: cloneDeep(resource),
      }
    );
  };

  editFile = async (resource: FileModel) => {
    const { EditFileModalComponent } =
      await import('../../modules/file/components/edit-file-modal.component');

    return this.modalService.open(
      EditFileModalComponent,
      {
        resource: cloneDeep(resource),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  lockFile = (rid: number) =>
    this.modalService.openConfirmation({
      title: 'WEB_DIALOGS_FILE_LOCK',
      text: 'WEB_DIALOGS_CONFIRM',
      confirmLabel: 'WEB_ACTIONS_LOCK',
      icon: 'bi-lock-fill',
      successText: 'WEB_NOTIFY_LOCK_SUCCESS',
      action: () => this.lockFileApi(rid),
    });

  compareFilesUpload = async (
    duplicateNames: string[],
    kind: 'file' | 'folder'
  ) => {
    const { CompareFilesModalComponent } =
      await import('../../shared/components/file/compare-files-modal.component');

    return this.modalService.open(
      CompareFilesModalComponent,
      {
        duplicateNames,
        kind,
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  privaSphereSend = async (resource: FileModel) => {
    const { PrivaSphereSendModalComponent } =
      await import('../../modules/file/components/privasphere-send-modal.component');

    return this.modalService.open(
      PrivaSphereSendModalComponent,
      {
        resource: cloneDeep(resource),
      },
      {
        modalDialogClass: 'privasphere-dialog',
      }
    );
  };

  openProtectedFileDialog = async (rid: number, version: number) => {
    const { ProtectedFileModalComponent } =
      await import('../../modules/file/components/protected-file-modal.component');

    return this.modalService.open(ProtectedFileModalComponent, {
      rid,
      version,
    });
  };

  openProtectedFileDownloadDialog = async (resource: FileModel) => {
    const { ProtectedFileDownloadModalComponent } =
      await import('../../modules/file/components/protected-file-download-modal.component');

    return this.modalService.open(ProtectedFileDownloadModalComponent, {
      resource: cloneDeep(resource),
    });
  };

  newShortcut = async (resource: FileModel) => {
    const { NewShortcutModalComponent } =
      await import('../../shared/components/file/new-shortcut-modal.component');

    return this.modalService.open(
      NewShortcutModalComponent,
      {
        resourceToLink: cloneDeep(resource),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  openAisSmsDialog = async (url: string) => {
    const { AisSmsModalComponent } =
      await import('../../shared/components/file/ais-sms-modal.component');

    return this.modalService.open(
      AisSmsModalComponent,
      {
        url,
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  unlockFile = (rid: number) =>
    this.modalService.openConfirmation({
      title: 'WEB_DIALOGS_FILE_UNLOCK',
      text: 'WEB_DIALOGS_CONFIRM',
      confirmLabel: 'WEB_ACTIONS_UNLOCK',
      icon: 'bi-lock-fill',
      successText: 'WEB_NOTIFY_UNLOCK_SUCCESS',
      action: () => this.unlockFileApi(rid),
    });

  unlockFileApi = (rid: number) =>
    this.http.post<FileModel>(`${API_URL}/files/${rid}/unlock`, null);

  lockFileApi = (rid: number) =>
    this.http.post<FileModel>(`${API_URL}/files/${rid}/lock`, null);

  addOrganisationSignature = (rid: number) =>
    this.modalService.openConfirmation({
      title: 'WEB_ACTIONS_ADD_ORGANISATION_SIGNATURE',
      text: 'WEB_DIALOGS_CONFIRM',
      request: { url: `files/${rid}/signseal`, method: 'post' },
      successText: 'WEB_NOTIFY_ORGANISATION_SIGNATURE_SUCCESS',
    });

  voteOnFile = (rid: number, decision: number, comment?: string) =>
    this.http.post<FileModel>(`${API_URL}/files/${rid}/vote`, {
      decision,
      comment,
    });

  deleteVersion = (rid: number, version: number) =>
    this.http.delete(`${API_URL}/files/${rid}/versions/${version}`);

  getOngoingApprovals = (params?: Record<string, string | number>) =>
    this.http.get<OngoingApproval[]>(`${API_URL}/files/ongoingapproval`, {
      params,
    });

  getApprovalUserLog = (params?: Record<string, string | number>) =>
    this.http.get<ApprovalUserLog[]>(`${API_URL}/files/approvaluserlog`, {
      params,
    });

  unzipFile = (rid: number, body: Nullable<FormData>) =>
    this.http.post(`${API_URL}/files/${rid}/unzip`, body);

  createFileByPath = (
    path: string,
    rid: number,
    body: Record<string, unknown>
  ) => this.http.post(`${API_URL}/${path}/${rid}/files`, body);

  unzipFileModal = async (rid: number) => {
    const { UnzipFileModalComponent } =
      await import('../../shared/components/file/unzip-file-modal.component');

    return this.modalService.open(
      UnzipFileModalComponent,
      {
        rid,
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  deleteVersionModal = (version: Version, subtype: number) => {
    const isFile = subtype === FileSubType.FILE;

    return this.modalService.openConfirmation({
      title: isFile
        ? 'WEB_DIALOGS_FILE_VERSION_DELETE'
        : 'WEB_DIALOGS_NOTE_VERSION_DELETE',
      text: 'WEB_DIALOGS_CONFIRM',
      confirmLabel: 'WEB_ACTIONS_DELETE',
      danger: true,
      successText: isFile
        ? 'WEB_NOTIFY_FILE_VERSION_DELETE_SUCCESS'
        : 'WEB_NOTIFY_NOTE_VERSION_DELETE_SUCCESS',
      action: () => this.deleteVersion(version.rid, version.version),
      result: version.rid,
    });
  };

  restoreVersion = async (version: Version, subtype: number) => {
    const { RestoreVersionModalComponent } =
      await import('../../modules/file/components/restore-version-modal.component');

    return this.modalService.open(RestoreVersionModalComponent, {
      version,
      subtype,
    });
  };

  uploadFileVersionModal = async (resource: FileModel) => {
    const { UploadVersionModalComponent } =
      await import('../../modules/file/components/upload-version-modal.component');

    return this.modalService.open(
      UploadVersionModalComponent,
      {
        resource: cloneDeep(resource),
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  voteFile = async (resource: FileModel, decision: number) => {
    const { FileVoteModalComponent } =
      await import('../../modules/file/components/file-vote-modal.component');

    return this.modalService.open(
      FileVoteModalComponent,
      {
        resource: cloneDeep(resource),
        decision,
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };
}
